import { memo, useCallback, useEffect, useState } from "react";

import {
  createTenantBankAccountAPI,
  getTenantBankAccountsAPI,
  updateTenantBankAccountAPI,
} from "services/api/tenants/api";
import { TenantBankAccountEditFormValues } from "services/api/tenants/types";
import { initialTenantBankAccountEditForm } from "services/api/tenants/consts";
import { Errors } from "services/forms/validation/types";
import { validateYup } from "services/forms/validation/validation";
import { useHandleTextFieldChange } from "services/forms/hooks";

import { TenantBankAccountEditProps } from "./types";
import { TENANT_BANK_ACCOUNT_EDIT_FORM_SCHEMA } from "./validation";

import { Button } from "ui/components/Button/Button";
import { Loader } from "ui/components/Loader";
import { Modal } from "ui/components/Modal";
import ModalHeader from "ui/components/Modal/ModalHeader";
import ModalBody from "ui/components/Modal/ModalBody";
import { useTranslate } from "helpers";
import { useDialog } from "ui/modules/public/components/DialogProvider/DialogProvider";
import IbanTextField from "ui/components/TextField/IbanTextField/IbanTextField";
import { getBankFromHrIban } from "ui/components/TextField/IbanTextField/ibanUtils";
import { TextField } from "ui/components/TextField/TextField";
import { CheckBox } from "ui/components/CheckBox";

const TenantBankAccountEditCard: React.FC<TenantBankAccountEditProps> = (props) => {
  const { tenantId, bankAccountId, show, onSuccess, onClose } = props;

  const { t } = useTranslate();
  const { createDialog } = useDialog();
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formValues, setFormValues] = useState<TenantBankAccountEditFormValues>(
    initialTenantBankAccountEditForm
  );

  useEffect(() => {
    if (show) {
      fetchBankAccount();
    }
  }, [show, bankAccountId]);

  const fetchBankAccount = async () => {
    if (!bankAccountId) {
      setFormValues(initialTenantBankAccountEditForm);
      setErrors({});
      return;
    }

    setIsLoading(true);
    try {
      const accounts = await getTenantBankAccountsAPI(tenantId);
      const account = accounts.find((a) => a.id === bankAccountId);
      if (account) {
        setFormValues({
          iban: account.iban,
          bankName: account.bankName,
          description: account.description,
          primary: account.primary,
        });
      }
    } catch {
      // error handled by snackbar
    }
    setIsLoading(false);
    setErrors({});
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      TENANT_BANK_ACCOUNT_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) return;

    setIsLoading(true);
    try {
      if (bankAccountId) {
        await updateTenantBankAccountAPI(tenantId, bankAccountId, formValues);
      } else {
        await createTenantBankAccountAPI(tenantId, formValues);
      }
      if (onSuccess) onSuccess();
    } catch {
      setIsLoading(false);
      return;
    }
    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  // Auto-popuni bankName iz IBAN-a
  useEffect(() => {
    const bank = getBankFromHrIban(formValues.iban);
    setFormValues((prev) => ({ ...prev, bankName: bank?.name ?? null }));
  }, [formValues.iban]);

  const handlePrimaryChange = useCallback(() => {
    setFormValues((old) => ({ ...old, primary: !old.primary }));
  }, []);

  const handleCloseModal = () => {
    createDialog({
      onConfirm: onClose,
    });
  };

  return (
    <Modal show={show} onHide={onClose} position="centered" size="small">
      <ModalHeader title="Unos poslovnog računa" icon="/media/images/add-icon.svg" onHide={onClose} />
      <ModalBody>
        <div className="flex gap-4">
          <div className="w-full m-auto">
            <div className="flex gap-3 h-full">
              <form noValidate className="text-left flex-1 p-4">
                <div className="">
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <IbanTextField
                        type="text"
                        name="iban"
                        label="IBAN"
                        placeholder="HR1234567890123456789"
                        onChange={handleTextFieldChange}
                        error={errors.iban}
                        value={formValues.iban}
                        required
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <TextField
                        type="text"
                        name="description"
                        label="Opis"
                        placeholder="npr. Glavni poslovni račun"
                        onChange={handleTextFieldChange}
                        error={errors.description}
                        value={formValues.description}
                      />
                    </div>
                  </div>
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <CheckBox
                        name="primary"
                        onChange={handlePrimaryChange}
                        checked={Boolean(formValues.primary)}
                        label="Primarni račun"
                        error={errors.primary}
                      />
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 fixed left-0 bottom-0 bg-slate-50 border border-t-slate-200 w-full flex justify-end gap-3">
                  <Button
                    variant="outlined"
                    children={t("admin.close")}
                    onClick={handleCloseModal}
                  />
                  <Button
                    variant="primary"
                    children={t("admin.save")}
                    onClick={handleFormSubmit}
                  />
                </div>
              </form>
            </div>
          </div>
        </div>

        <Loader isLoading={isLoading ?? false}> </Loader>
      </ModalBody>
    </Modal>
  );
};
export default memo(TenantBankAccountEditCard);
